import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChatMessage as ChatMessageComponent } from '@/components/chat/ChatMessage';
import { useSupervisor } from '@/hooks/useSupervisor';
import { getAiProposedUpdate } from '@/services/api';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { API_BASE_URL } from '@/config/apiBaseUrl';
import {
  addMessage,
  updateMessage,
  Message,
  selectChatMessages,
  selectChatMode,
  requestHumanSupport,
  returnToNormalMode,
  ChatMode,
  addAdminMessages,
  setChatMode,
} from '@/features/chat/chatSlice';
import { sanitizeInput, sanitizeMessage } from '@/utils/security';
import i18n from '@/i18n';
import { logging } from '@/services/logging';
import { useToast } from '@/hooks/use-toast';

const itemFieldsThatMatter: readonly string[] = [
  'uploadedFileId',
  'paperTypeId',
  'printerId',
  'width',
  'height',
  'widthIn',
  'heightIn',
  'originalWidthPx',
  'originalHeightPx',
];

const hasMeaningfulItem = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return itemFieldsThatMatter.some((field) => {
    const candidate = (value as Record<string, unknown>)[field];
    return candidate !== undefined && candidate !== null && candidate !== '';
  });
};

const hasMeaningfulShippingAddress = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return Object.values(value as Record<string, unknown>).some(
    (candidate) => candidate !== undefined && candidate !== null && candidate !== ''
  );
};

const hasMeaningfulOrderPatch = (
  patch: Record<string, unknown> | undefined
): boolean => {
  if (!patch) {
    return false;
  }

  if (Array.isArray(patch.items) && patch.items.some(hasMeaningfulItem)) {
    return true;
  }

  if (hasMeaningfulShippingAddress(patch.shippingAddress)) {
    return true;
  }

  return false;
};

const ADMIN_REPLY_FAST_POLL_MS = 5_000;
const ADMIN_REPLY_SLOW_POLL_MS = 60_000;
const ADMIN_REPLY_FAST_WINDOW_MS = 60_000;

export const ChatPanel = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { state: supervisorState, send: sendToSupervisor } = useSupervisor();
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectChatMessages);
  const chatMode = useAppSelector(selectChatMode);
  const { user, temporaryUserId } = useAppSelector((state: RootState) => state.auth);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastAdminReplyIdRef = useRef<string | null>(null);
  const adminReplyFastPollingUntilRef = useRef<number>(0);
  const hasHydratedScrollRef = useRef(false);

  const extendAdminReplyFastPollingWindow = () => {
    adminReplyFastPollingUntilRef.current = Date.now() + ADMIN_REPLY_FAST_WINDOW_MS;
  };

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const params = new URLSearchParams();
        if (user?.id) params.append('userId', user.id);
        if (temporaryUserId) params.append('temporaryUserId', temporaryUserId);

        const response = await fetch(`${API_BASE_URL}/chat/history?${params.toString()}`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await response.json();
        if (Array.isArray(data.messages) && data.messages.length > 0) {
          const restored = data.messages
            .map((msg: any) => {
              if (!msg || typeof msg.id !== 'string' || typeof msg.content !== 'string') {
                return null;
              }
              return {
                id: msg.id,
                author: msg.author === 'user' ? 'user' : 'ai',
                content: msg.content,
                mode: typeof msg.mode === 'string' ? msg.mode : undefined,
                uiPayload: msg.uiPayload,
              } as Message;
            })
            .filter((msg: Message | null): msg is Message => Boolean(msg));
          if (restored.length > 0) {
            dispatch(addAdminMessages(restored));
          }
        }
      } catch (error) {
        logging.error('Failed to fetch chat history', { error });
      } finally {
        setHistoryLoaded(true);
      }
    };

    setHistoryLoaded(false);
    void fetchHistory();
  }, [dispatch, user?.id, temporaryUserId]);

  useEffect(() => {
    const fetchInitialMode = async () => {
      try {
        const params = new URLSearchParams();
        if (user?.id) params.append('userId', user.id);
        if (temporaryUserId) params.append('temporaryUserId', temporaryUserId);

        const response = await fetch(`${API_BASE_URL}/chat/mode?${params.toString()}`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await response.json();
        if (data.mode) {
          dispatch(setChatMode(data.mode));
        }
      } catch (error) {
        logging.error('Failed to fetch initial chat mode', { error });
      }
    };
    void fetchInitialMode();
  }, [dispatch, user?.id, temporaryUserId]);

  useEffect(() => {
    const initialContent = t('chat.initialMessage');
    const initialActions = [t('chat.options.printArt'), t('chat.options.aboutCompany')];

    if (!historyLoaded) {
      return;
    }

    if (messages.length === 0) {
      dispatch(
        addMessage({
          author: 'ai',
          content: initialContent,
          suggestedActions: initialActions,
        })
      );
      return;
    }

    const firstMessage = messages[0];
    if (firstMessage.author !== 'ai') {
      return;
    }

    const shouldUpdateContent = firstMessage.content !== initialContent;
    const shouldUpdateActions = !Array.isArray(firstMessage.suggestedActions)
      || firstMessage.suggestedActions.length !== initialActions.length
      || firstMessage.suggestedActions.some((action, index) => action !== initialActions[index]);

    if (shouldUpdateContent || shouldUpdateActions) {
      dispatch(
        updateMessage({
          id: firstMessage.id,
          changes: {
            content: initialContent,
            suggestedActions: initialActions,
          },
        })
      );
    }
  }, [dispatch, historyLoaded, messages, t]);

  useLayoutEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: hasHydratedScrollRef.current ? 'smooth' : 'auto',
      block: 'end',
    });
    hasHydratedScrollRef.current = true;
    inputRef.current?.focus();
  }, [messages.length]);


  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const getNextPollDelay = (): number => {
      return Date.now() < adminReplyFastPollingUntilRef.current
        ? ADMIN_REPLY_FAST_POLL_MS
        : ADMIN_REPLY_SLOW_POLL_MS;
    };

    const scheduleNextPoll = (delay: number) => {
      if (cancelled) {
        return;
      }
      timeout = setTimeout(() => {
        void pollAdminReplies();
      }, delay);
    };

    const pollAdminReplies = async () => {
      try {
        const params = new URLSearchParams();
        if (user?.id) params.append('userId', user.id);
        if (temporaryUserId) params.append('temporaryUserId', temporaryUserId);
        if (lastAdminReplyIdRef.current) params.append('lastFetchedId', lastAdminReplyIdRef.current);

        const response = await fetch(`${API_BASE_URL}/chat/admin-replies?${params.toString()}`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await response.json();
        
        if (data.replies && data.replies.length > 0) {
          extendAdminReplyFastPollingWindow();
          data.replies.forEach((reply: any) => {
            const content = typeof reply?.content === 'string' ? reply.content.trim() : '';
            if (!content) {
              return;
            }
            const adminName = typeof reply?.adminName === 'string' && reply.adminName.trim().length > 0
              ? reply.adminName.trim()
              : t('chat.adminReplyToastFallbackAuthor', 'Admin');
            toast({
              title: t('chat.adminReplyToastTitle', 'New message from {{name}}', { name: adminName }),
              description: content,
            });
          });
          const formattedMessages: Message[] = data.replies.map((reply: any) => ({
            id: reply.id,
            author: 'ai',
            content: reply.content,
            mode: reply.mode,
            uiPayload: {
              adminName: typeof reply.adminName === 'string' ? reply.adminName : undefined,
            },
          }));
          dispatch(addAdminMessages(formattedMessages));
          const latestReply = data.replies[data.replies.length - 1];
          if (latestReply?.id) {
            lastAdminReplyIdRef.current = latestReply.id;
          }
          if (latestReply?.mode === 'AI' || latestReply?.mode === 'WAITING_FOR_AGENT' || latestReply?.mode === 'HUMAN') {
            dispatch(setChatMode(latestReply.mode));
          }
        }
      } catch (error) {
        logging.error('Failed to poll admin replies', { error });
      } finally {
        scheduleNextPoll(getNextPollDelay());
      }
    };

    if (historyLoaded && (user?.id || temporaryUserId)) {
      const knownAdminReplyIds = messages
        .filter((message) => message.author === 'ai' && typeof message.uiPayload?.adminName === 'string')
        .map((message) => message.id);
      if (knownAdminReplyIds.length > 0) {
        lastAdminReplyIdRef.current = knownAdminReplyIds[knownAdminReplyIds.length - 1];
      }
      void pollAdminReplies();
    }

    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [historyLoaded, messages, dispatch, t, toast, user?.id, temporaryUserId]);

  useEffect(() => {
    if (chatMode === 'WAITING_FOR_AGENT' || chatMode === 'HUMAN') {
      extendAdminReplyFastPollingWindow();
    }
  }, [chatMode]);

  const buildConversationHistory = (userContent: string) => {
    const history = messages.map((message) => ({
      role: message.author === 'ai' ? ('assistant' as const) : ('user' as const),
      content:
        typeof message.content === 'string'
          ? message.content
          : JSON.stringify(message.content),
    }));

    history.push({ role: 'user' as const, content: userContent });
    return history;
  };

  const sendMessage = async (rawContent: string, { fromSuggestedAction = false } = {}) => {
    const trimmed = rawContent.trim();
    if (!trimmed || isLoading) {
      return;
    }

    setIsLoading(true);
    const sanitizedContent = sanitizeMessage(trimmed);
    dispatch(addMessage({ author: 'user', content: sanitizedContent }));

    if (chatMode === 'WAITING_FOR_AGENT' || chatMode === 'HUMAN') {
      extendAdminReplyFastPollingWindow();
    }

    if (!fromSuggestedAction) {
      setInput('');
    }

    try {
      const conversationHistory = buildConversationHistory(sanitizedContent as string);
      const proposedUpdate = await getAiProposedUpdate(
        conversationHistory,
        supervisorState.context.order,
        i18n.language
      );

      if (proposedUpdate.mode && proposedUpdate.mode !== chatMode) {
        dispatch(setChatMode(proposedUpdate.mode as ChatMode));
      }

      const supervisorIsIdle = supervisorState.matches('idle');
      const proposedPatch = proposedUpdate.proposed_update as Record<string, unknown> | undefined;
      const patchHasMeaningfulContent = hasMeaningfulOrderPatch(proposedPatch);

      const shouldShowUpdateOrderMessage =
        proposedUpdate.intent !== 'update_order' || patchHasMeaningfulContent;
      const trimmedUtterance = typeof proposedUpdate.utterance === 'string'
        ? proposedUpdate.utterance.trim()
        : '';
      const suggestedActions = Array.isArray(proposedUpdate.suggestedActions)
        ? proposedUpdate.suggestedActions
        : [];
      const sanitizedSuggestedActions = suggestedActions.length > 0
        ? suggestedActions.map(sanitizeInput)
        : undefined;
      const hasSuggestedActions = suggestedActions.length > 0;
      const shouldRenderAssistantMessage = trimmedUtterance.length > 0 || hasSuggestedActions;

      if (shouldShowUpdateOrderMessage && shouldRenderAssistantMessage) {
        dispatch(
          addMessage({
            author: 'ai',
            content: trimmedUtterance,
            suggestedActions: sanitizedSuggestedActions,
          })
        );
      }

      if (
        proposedUpdate.intent === 'update_order'
        && proposedUpdate.proposed_update
        && (supervisorIsIdle || patchHasMeaningfulContent)
      ) {
        sendToSupervisor({
          type: 'ORDER.APPLY_PATCH',
          patch: proposedUpdate.proposed_update,
        });
      }

      if (proposedUpdate.interfaceGuidance?.gotoStep) {
        sendToSupervisor({
          type: 'UI.GOTO_STEP',
          step: proposedUpdate.interfaceGuidance.gotoStep,
        });
      }
    } catch (error) {
      logging.error('Failed to get AI response', { error });
      dispatch(
        addMessage({
          author: 'ai',
          content: 'Sorry, I encountered an error.',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void sendMessage(input);
  };

  const handleSuggestedActionClick = (action: string) => {
    void sendMessage(action, { fromSuggestedAction: true });
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      <div className="flex-grow overflow-y-auto p-4" ref={scrollAreaRef}>
        <div className="space-y-2">
          {messages.map((message: Message) => (
            <ChatMessageComponent
              key={message.id}
              message={message}
              onSuggestedActionClick={handleSuggestedActionClick}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
      <div className="p-4 border-t dark:border-gray-700">
        <form onSubmit={handleSubmit} className="flex items-center space-x-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={t('chat.placeholder', 'Type your message...')}
            className="flex-1"
            disabled={isLoading}
          />
          <Button type="submit" disabled={isLoading}>
            {isLoading 
              ? t('chat.sending', 'Sending...') 
              : chatMode === 'AI' 
                ? t('chat.send', 'Send') 
                : t('chat.sendToAssistant', 'Send to assistant')}
          </Button>
        </form>
        <div className="mt-2 text-center">
          {chatMode === 'AI' ? (
            <button
              type="button"
              onClick={() => dispatch(requestHumanSupport())}
              className="text-xs text-indigo-400 hover:text-indigo-300 underline transition-colors"
            >
              {t('chat.humanSupport.request', 'Need help from a human? Chat with support')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => dispatch(returnToNormalMode())}
              className="text-xs text-amber-400 hover:text-amber-300 underline transition-colors"
            >
              {t('chat.humanSupport.return', 'Return to normal AI mode')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
